// =============================================================================
// Acessos — gestores e os centros de custo de cada um
//
// Até 18/09 dar acesso a um gestor exigia um desenvolvedor rodando script. A
// tela de login já dizia "peça ao Financeiro para cadastrar o seu e-mail" — e o
// Financeiro não tinha onde. Esta é a tela.
//
// O Financeiro só concede gestor (migration 027). Admin, financeiro e Dev são
// trocados só por um admin, pelo botão Trocar perfil (057). As regras moram nas
// RPCs; aqui a tela só evita oferecer o botão que a RPC recusaria.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FalhaApi } from "../lib/api.ts";
import type { Tom } from "../lib/formato.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { Modal } from "../componentes/Modal.tsx";

type CentroCusto = { id: string; codigo: string; nome: string };

type Acesso = {
  usuario_id: string;
  email: string;
  nome: string;
  ativo: boolean;
  ja_entrou: boolean;
  papeis: string[];
  centros_custo: CentroCusto[];
  atualizado_em: string;
};

const PAPEL: Record<string, { rotulo: string; tom: Tom }> = {
  admin: { rotulo: "Admin", tom: "atencao" },
  financeiro: { rotulo: "Financeiro", tom: "ok" },
  gestor_cc: { rotulo: "Gestor", tom: "neutro" },
  dev: { rotulo: "Dev", tom: "neutro" },
};

/** Os perfis que o admin troca por aqui. Gestor é pelo formulário de gestor. */
const PERFIS: { papel: string; rotulo: string; explica: string }[] = [
  { papel: "admin", rotulo: "Administrador", explica: "Vê tudo e opera o sistema: acessos, credencial do Conta Azul, correções." },
  { papel: "financeiro", rotulo: "Financeiro", explica: "Vê todos os centros de custo e trata as filas." },
  { papel: "dev", rotulo: "Dev", explica: "Vê só o staging, com dados sintéticos. Nenhum dado real chega à tela dele." },
];

// `atualizado_em` é INSTANTE (timestamptz), não data: aqui `new Date` é o
// certo, e o fuso vai explícito. Cortar a string em UTC mostraria o dia
// seguinte para alterações feitas depois das 21h (§3.6).
const DATA_SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** Financeiro, admin e Dev são geridos por admin, não pelo formulário de gestor. */
const gerenciavel = (a: Acesso) =>
  !a.papeis.some((p) => p === "financeiro" || p === "admin" || p === "dev");

/**
 * "Ativo" não é "tem acesso". Um cadastro ativo SEM papel entra no sistema e não
 * vê nada — a RLS nega tudo. Visto na primeira abertura da tela, em 18/09: um
 * usuário assim aparecia em "Com acesso", o que era mentira.
 */
const temAcesso = (a: Acesso) => a.ativo && a.papeis.length > 0;

type Formulario = { modo: "novo" | "editar" | "reativar"; acesso?: Acesso };

export function Acessos() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<"com" | "sem">("com");
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [desativando, setDesativando] = useState<Acesso | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [trocando, setTrocando] = useState<Acesso | null>(null);

  // A mesma consulta do App, pela mesma chave: vem do cache.
  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<{ papeis: string[] }>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  const souAdmin = eu.data?.papeis.includes("admin") ?? false;

  const q = useQuery({
    queryKey: ["acessos"],
    queryFn: () => api.get<{ usuarios: Acesso[] }>("/api/acessos"),
  });

  const usuarios = q.data?.usuarios ?? [];
  const comAcesso = useMemo(() => usuarios.filter(temAcesso), [usuarios]);
  const semAcesso = useMemo(() => usuarios.filter((u) => !temAcesso(u)), [usuarios]);
  const visiveis = filtro === "com" ? comAcesso : semAcesso;
  const gestoresAtivos = comAcesso.filter((u) => u.papeis.includes("gestor_cc")).length;

  const concluir = (texto: string) => {
    setFormulario(null);
    setDesativando(null);
    setTrocando(null);
    setAviso(texto);
    void qc.invalidateQueries({ queryKey: ["acessos"] });
  };

  return (
    <div className="pagina pb-20">
      <header className="flex flex-wrap items-end justify-between gap-4 py-7">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
            Acessos
          </p>
          <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
            Gestores e centros de custo
          </h1>
          <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
            Quem entra no sistema e o que cada gestor enxerga. O gestor vê só os centros de custo
            marcados aqui; financeiro e admin veem todos; Dev vê só o staging, com dados sintéticos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setAviso(null); setFormulario({ modo: "novo" }); }}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul"
        >
          Cadastrar gestor
        </button>
      </header>

      {aviso && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta"
        >
          {aviso}
        </p>
      )}

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}

      {q.isError && (
        <Vazio
          titulo="Não foi possível carregar os acessos"
          dica={(q.error as { message?: string })?.message}
        />
      )}

      {q.data && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Chip ativo={filtro === "com"} onClick={() => setFiltro("com")}>
              Com acesso · {comAcesso.length}
            </Chip>
            <Chip ativo={filtro === "sem"} onClick={() => setFiltro("sem")}>
              Sem acesso · {semAcesso.length}
            </Chip>
            <span className="num ml-auto text-[12.5px] text-tinta-fraca">
              {gestoresAtivos} {gestoresAtivos === 1 ? "gestor ativo" : "gestores ativos"}
            </span>
          </div>

          {visiveis.length === 0 ? (
            <Painel>
              <Vazio
                titulo={filtro === "com" ? "Ninguém com acesso ainda" : "Ninguém sem acesso"}
                dica={
                  filtro === "com"
                    ? "Cadastre o gestor com o e-mail corporativo e marque os centros de custo dele. No primeiro login com o Google, o acesso já estará liberado."
                    : "Quem for desativado aparece aqui, e o acesso pode ser devolvido a qualquer momento."
                }
              />
            </Painel>
          ) : (
            <ul className="grid gap-2">
              {visiveis.map((a) => (
                <LinhaAcesso
                  key={a.usuario_id}
                  acesso={a}
                  onEditar={() => { setAviso(null); setFormulario({ modo: "editar", acesso: a }); }}
                  onReativar={() => { setAviso(null); setFormulario({ modo: "reativar", acesso: a }); }}
                  onDesativar={() => { setAviso(null); setDesativando(a); }}
                  onTrocarPerfil={souAdmin ? () => { setAviso(null); setTrocando(a); } : undefined}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {formulario && (
        <ModalGestor
          formulario={formulario}
          onFechar={() => setFormulario(null)}
          onSalvo={(r, nome) =>
            concluir(
              r.criado
                ? `${nome} cadastrado. No primeiro login com o Google, o acesso já estará liberado.`
                : r.alterado
                  ? `Acesso de ${nome} atualizado.`
                  : `Nada mudou no acesso de ${nome}.`,
            )
          }
        />
      )}

      {trocando && (
        <ModalPerfil
          acesso={trocando}
          onFechar={() => setTrocando(null)}
          onTrocado={(r) =>
            concluir(
              r.alterado
                ? `${trocando.nome} agora é ${PERFIS.find((p) => p.papel === r.papel)?.rotulo ?? r.papel}.`
                : `Nada mudou: ${trocando.nome} já tinha esse perfil.`,
            )
          }
        />
      )}

      {desativando && (
        <ModalDesativar
          acesso={desativando}
          onFechar={() => setDesativando(null)}
          onDesativado={() => concluir(`${desativando.nome} não tem mais acesso ao sistema.`)}
        />
      )}
    </div>
  );
}

function LinhaAcesso({
  acesso: a,
  onEditar,
  onReativar,
  onDesativar,
  onTrocarPerfil,
}: {
  acesso: Acesso;
  onEditar: () => void;
  onReativar: () => void;
  onDesativar: () => void;
  /** Só para quem é admin. */
  onTrocarPerfil?: () => void;
}) {
  const podeMexer = gerenciavel(a);
  const veTudo = a.papeis.some((p) => p === "financeiro" || p === "admin");
  const dev = a.papeis.includes("dev");

  return (
    <li className="rounded-lg border border-linha bg-superficie p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[260px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[14.5px] font-bold">{a.nome}</h2>
            {a.papeis.map((p) => (
              <Pilula key={p} tom={PAPEL[p]?.tom ?? "neutro"}>{PAPEL[p]?.rotulo ?? p}</Pilula>
            ))}
            {!a.ativo && <Pilula tom="risco">Desativado</Pilula>}
            {a.ativo && a.papeis.length === 0 && <Pilula tom="risco">Sem papel</Pilula>}
            {/* Cadastrado mas nunca entrou: quase sempre e-mail com erro de
                digitação, ou a pessoa que ainda não foi avisada. */}
            {temAcesso(a) && !a.ja_entrou && <Pilula tom="atencao">Ainda não entrou</Pilula>}
          </div>
          <p className="mt-0.5 text-[12.5px] text-tinta-fraca">{a.email}</p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {veTudo ? (
              <span className="text-[12.5px] text-tinta-media">Vê todos os centros de custo</span>
            ) : dev ? (
              <span className="text-[12.5px] text-tinta-media">Vê só o staging, com dados sintéticos</span>
            ) : a.centros_custo.length === 0 ? (
              <span className="text-[12.5px] text-tinta-fraca">Sem centro de custo</span>
            ) : (
              a.centros_custo.map((cc) => (
                <span
                  key={cc.id}
                  className="num rounded-lg border border-linha bg-superficie-2 px-2 py-0.5 text-[12px] text-tinta-media"
                >
                  {cc.codigo} · {cc.nome}
                </span>
              ))
            )}
          </div>
          <p className="num mt-2 text-[11.5px] text-tinta-fraca">
            alterado em {DATA_SP.format(new Date(a.atualizado_em))}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          {a.ativo && onTrocarPerfil && <Botao onClick={onTrocarPerfil}>Trocar perfil</Botao>}
          {!podeMexer ? (
            !onTrocarPerfil && (
              <span className="max-w-[180px] text-right text-[12px] text-tinta-fraca">
                Financeiro, admin e Dev são alterados só por um admin.
              </span>
            )
          ) : temAcesso(a) ? (
            <>
              <Botao onClick={onEditar}>Editar</Botao>
              <Botao onClick={onDesativar} perigo>Desativar</Botao>
            </>
          ) : (
            <Botao onClick={onReativar}>Dar acesso</Botao>
          )}
        </div>
      </div>
    </li>
  );
}

function ModalGestor({
  formulario,
  onFechar,
  onSalvo,
}: {
  formulario: Formulario;
  onFechar: () => void;
  onSalvo: (r: { criado: boolean; alterado: boolean }, nome: string) => void;
}) {
  const a = formulario.acesso;
  const [email, setEmail] = useState(a?.email ?? "");
  const [nome, setNome] = useState(a?.nome ?? "");
  // Reativar começa sem CC marcado: o vínculo antigo foi desfeito ao desativar,
  // e quem reativa precisa decidir de novo o que a pessoa vai ver.
  const [marcados, setMarcados] = useState<Set<string>>(
    () => new Set(formulario.modo === "editar" ? (a?.centros_custo ?? []).map((c) => c.id) : []),
  );

  const cadastros = useQuery({
    queryKey: ["cadastros"],
    queryFn: () => api.get<{ centros: CentroCusto[] }>("/api/cadastros"),
  });

  const salvar = useMutation({
    mutationFn: () =>
      api.post<{ criado: boolean; alterado: boolean }>("/api/gestores", {
        email: email.trim(),
        nome: nome.trim(),
        centros_custo: [...marcados],
      }),
    onSuccess: (r) => onSalvo(r, nome.trim()),
  });

  const alternar = (id: string) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });

  // A RPC recusa cada um destes com a mesma explicação; impedir aqui só poupa
  // uma ida e volta e diz o que falta antes do clique.
  const faltando = [
    !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && "o e-mail",
    nome.trim() === "" && "o nome",
    marcados.size === 0 && "ao menos um centro de custo",
  ].filter(Boolean) as string[];

  const titulo =
    formulario.modo === "novo" ? "Cadastrar gestor"
    : formulario.modo === "reativar" ? `Dar acesso a ${a?.nome}`
    : `Editar ${a?.nome}`;

  return (
    <Modal titulo={titulo} onFechar={onFechar}>
      <label className="block">
        <span className="text-[12px] font-semibold text-tinta-media">E-mail corporativo</span>
        <input
          type="email"
          value={email}
          readOnly={formulario.modo !== "novo"}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nome@contabilidadefacilitada.com"
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px] read-only:bg-superficie-2 read-only:text-tinta-fraca"
        />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          {formulario.modo === "novo"
            ? "É por ele que o login com o Google reconhece a pessoa no primeiro acesso."
            : "O e-mail não muda: é a identidade da pessoa no Histórico."}
        </span>
      </label>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Nome</span>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        />
      </label>

      <fieldset className="mt-4">
        <legend className="text-[12px] font-semibold text-tinta-media">
          Centros de custo que o gestor vai ver
        </legend>
        {cadastros.isPending && <p className="mt-1.5 text-[12.5px] text-tinta-fraca">Carregando…</p>}
        <div className="thin-scroll mt-1.5 grid max-h-64 gap-1 overflow-y-auto">
          {(cadastros.data?.centros ?? []).map((cc) => (
            <label
              key={cc.id}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 ${
                marcados.has(cc.id) ? "border-laranja bg-superficie" : "border-linha"
              }`}
            >
              <input type="checkbox" checked={marcados.has(cc.id)} onChange={() => alternar(cc.id)} />
              <span className="num text-[13px] font-semibold">{cc.codigo}</span>
              <span className="text-[13px] text-tinta-media">{cc.nome}</span>
            </label>
          ))}
        </div>
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          O que ficar desmarcado deixa de aparecer para o gestor na próxima vez que a tela carregar.
        </span>
      </fieldset>

      {salvar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(salvar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        {faltando.length > 0 && (
          <span className="mr-auto text-[12px] text-tinta-fraca">Falta {faltando.join(", ")}.</span>
        )}
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button
          type="button"
          disabled={faltando.length > 0 || salvar.isPending}
          onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
        >
          {salvar.isPending ? "Salvando…" : formulario.modo === "reativar" ? "Dar acesso" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

function ModalDesativar({
  acesso,
  onFechar,
  onDesativado,
}: {
  acesso: Acesso;
  onFechar: () => void;
  onDesativado: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const desativar = useMutation({
    mutationFn: () =>
      api.post(`/api/gestores/${acesso.usuario_id}/desativar`, { motivo: motivo.trim() || null }),
    onSuccess: onDesativado,
  });

  return (
    <Modal titulo={`Desativar ${acesso.nome}`} onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media">
        O acesso acaba na próxima vez que a tela dele carregar dados, sem esperar a sessão expirar.
        O cadastro continua existindo, porque ele aparece no Histórico. Para devolver o acesso, use
        Dar acesso, na lista de quem está sem acesso.
      </p>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Motivo</span>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          placeholder="Ex.: saiu da empresa, mudou de área."
          className="mt-1 w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          Fica no Histórico de Movimentações.
        </span>
      </label>

      {desativar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(desativar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button
          type="button"
          disabled={desativar.isPending}
          onClick={() => desativar.mutate()}
          className="rounded-lg border border-risco-linha bg-risco-bg px-4 py-2 text-[13px] font-bold text-risco-tinta disabled:opacity-45"
        >
          {desativar.isPending ? "Desativando…" : "Desativar"}
        </button>
      </div>
    </Modal>
  );
}

function ModalPerfil({
  acesso,
  onFechar,
  onTrocado,
}: {
  acesso: Acesso;
  onFechar: () => void;
  onTrocado: (r: { papel: string; alterado: boolean }) => void;
}) {
  const atual = PERFIS.find((p) => acesso.papeis.includes(p.papel))?.papel ?? "";
  const [papel, setPapel] = useState(atual === "dev" ? "" : "dev");
  const [motivo, setMotivo] = useState("");
  const trocar = useMutation({
    mutationFn: () =>
      api.post<{ papel: string; alterado: boolean }>(`/api/usuarios/${acesso.usuario_id}/perfil`, {
        papel,
        motivo: motivo.trim(),
      }),
    onSuccess: onTrocado,
  });

  return (
    <Modal titulo={`Trocar o perfil de ${acesso.nome}`} onFechar={onFechar}>
      <fieldset>
        <legend className="text-[12px] font-semibold text-tinta-media">Perfil</legend>
        <div className="mt-1.5 grid gap-1">
          {PERFIS.map((p) => (
            <label
              key={p.papel}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 ${
                papel === p.papel ? "border-laranja bg-superficie" : "border-linha"
              }`}
            >
              <input
                type="radio"
                name="perfil"
                className="mt-1"
                checked={papel === p.papel}
                onChange={() => setPapel(p.papel)}
              />
              <span>
                <span className="text-[13px] font-semibold">
                  {p.rotulo}
                  {p.papel === atual && <span className="ml-1.5 font-normal text-tinta-fraca">(atual)</span>}
                </span>
                <span className="block text-[12px] text-tinta-media">{p.explica}</span>
              </span>
            </label>
          ))}
        </div>
        {acesso.papeis.includes("gestor_cc") && (
          <span className="mt-1 block text-[11.5px] text-tinta-fraca">
            Ao trocar, os centros de custo de gestor deixam de valer.
          </span>
        )}
      </fieldset>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Motivo</span>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          placeholder="Ex.: desenvolvimento sem acesso a dado real."
          className="mt-1 w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          Obrigatório. Fica no Histórico de Movimentações.
        </span>
      </label>

      {trocar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(trocar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button
          type="button"
          disabled={!papel || motivo.trim() === "" || trocar.isPending}
          onClick={() => trocar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
        >
          {trocar.isPending ? "Trocando…" : "Trocar perfil"}
        </button>
      </div>
    </Modal>
  );
}

function Botao({
  onClick,
  perigo = false,
  children,
}: {
  onClick: () => void;
  perigo?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap ${
        perigo
          ? "border-linha text-risco-tinta hover:border-risco-linha hover:bg-risco-bg"
          : "border-linha hover:border-laranja hover:text-laranja-tinta"
      }`}
    >
      {children}
    </button>
  );
}

function Chip({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`rounded-lg border px-3 py-1 text-[12.5px] font-semibold ${
        ativo
          ? "border-azul bg-azul text-white"
          : "border-linha bg-superficie text-tinta-media hover:bg-superficie-2"
      }`}
    >
      {children}
    </button>
  );
}
